import settings from '@/constants/constants';
import { apiPatientPortalContract } from '@montugroup/pms-api-contracts';
import { initQueryClient } from '@ts-rest/react-query';
import type { z } from 'zod';

type SubmitConsentFormPostBody = z.infer<typeof apiPatientPortalContract.submitConsentForm.body>;

export type SubmitRequestForMedicalRecordsPayload = {
  /** Name of the health practitioner's clinic */
  healthPractitionerClinicName: string;
  /** Name of the health practitioner */
  healthPractitionerName: string;
  /** Patient's signature as a base64 string */
  signature: string;
  /** Data from url param to be passed to BE */
  paramData: string;
  /** Data kind from url param to be passed to BE */
  paramKind?: SubmitConsentFormPostBody['data']['identity']['kind'];
};

/**
 * Custom React hook to submit a patient's consent for us to gather health records on their behalf.
 */
export const useSubmitForMedicalRecordsConsent = () => {
  const tsRestClient = initQueryClient(apiPatientPortalContract, {
    baseUrl: settings.baseUrl,
  });

  const { mutateAsync, ...rest } = tsRestClient.submitConsentForm.useMutation();

  return {
    ...rest, // isPending, isSuccess, isError, etc.
    mutateAsync: (payload: SubmitRequestForMedicalRecordsPayload) => {
      const { healthPractitionerClinicName, healthPractitionerName, signature, paramData, paramKind } = payload;

      return mutateAsync({
        body: {
          data: {
            type: 'medical-records-consent',
            healthPractitionerClinicName,
            nameOfHealthPractitioner: healthPractitionerName,
            signature,
            identity: {
              kind: paramKind ?? 'patient-code',
              data: paramData,
            },
          },
        },
        // satisfy ts-rest optional keys
        query: {},
      });
    },
  };
};
